/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Interactive Before/After Image Comparison Slider Component (Task 10.4.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { ChevronsLeftRight } from 'lucide-react';

export interface BeforeAfterSliderProps {
  beforeImage: string;
  afterImage: string;
  beforeLabel?: string;
  afterLabel?: string;
  isTransparentAfter?: boolean;
  className?: string;
  initialPosition?: number;
  onPositionChange?: (position: number) => void;
}

export const BeforeAfterSlider: React.FC<BeforeAfterSliderProps> = ({
  beforeImage,
  afterImage,
  beforeLabel = 'Original',
  afterLabel = 'Enhanced',
  isTransparentAfter = false,
  className = '',
  initialPosition = 50,
  onPositionChange,
}) => {
  const [sliderPosition, setSliderPosition] = useState<number>(initialPosition);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const updatePositionFromClientX = useCallback(
    (clientX: number) => {
      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const rawX = clientX - rect.left;
      const percentage = Math.max(0, Math.min(100, (rawX / rect.width) * 100));
      const rounded = Number(percentage.toFixed(1));

      setSliderPosition(rounded);
      onPositionChange?.(rounded);
    },
    [onPositionChange]
  );

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
    updatePositionFromClientX(e.clientX);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    updatePositionFromClientX(e.clientX);
  };

  const handlePointerUp = () => {
    if (isDragging) {
      setIsDragging(false);
    }
  };

  // Keyboard navigation accessibility
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowLeft') {
      const next = Math.max(0, sliderPosition - 5);
      setSliderPosition(next);
      onPositionChange?.(next);
    } else if (e.key === 'ArrowRight') {
      const next = Math.min(100, sliderPosition + 5);
      setSliderPosition(next);
      onPositionChange?.(next);
    }
  };

  useEffect(() => {
    const handleGlobalMouseUp = () => setIsDragging(false);
    window.addEventListener('mouseup', handleGlobalMouseUp);
    window.addEventListener('touchend', handleGlobalMouseUp);
    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      window.removeEventListener('touchend', handleGlobalMouseUp);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      role="slider"
      aria-label="Before and After comparison slider"
      aria-valuenow={sliderPosition}
      aria-valuemin={0}
      aria-valuemax={100}
      onKeyDown={handleKeyDown}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      className={`relative w-full overflow-hidden rounded-2xl select-none cursor-ew-resize border border-surface-border bg-surface-elevated/40 shadow-glass ${className}`}
      style={{ touchAction: 'none' }}
    >
      {/* Background Checkerboard for Transparent Cutout Output */}
      {isTransparentAfter && (
        <div
          className="absolute inset-0 pointer-events-none opacity-40"
          style={{
            backgroundImage:
              'linear-gradient(45deg, #888 25%, transparent 25%), linear-gradient(-45deg, #888 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #888 75%), linear-gradient(-45deg, transparent 75%, #888 75%)',
            backgroundSize: '16px 16px',
            backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
          }}
        />
      )}

      {/* 1. Base Layer: After / Processed Result Image */}
      <img
        src={afterImage}
        alt={afterLabel}
        className="block w-full h-full object-contain pointer-events-none"
        draggable={false}
      />

      {/* 2. Top Layer: Before / Original Image (Clipped) */}
      <div
        className="absolute inset-0 overflow-hidden pointer-events-none"
        style={{
          clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`,
        }}
      >
        <img
          src={beforeImage}
          alt={beforeLabel}
          className="block w-full h-full object-contain pointer-events-none"
          draggable={false}
        />
      </div>

      {/* 3. Draggable Divider Line & Grip Handle */}
      <div
        className="absolute top-0 bottom-0 pointer-events-none z-20 flex items-center justify-center -translate-x-1/2"
        style={{ left: `${sliderPosition}%` }}
      >
        {/* Vertical Divider Bar */}
        <div className="w-[2px] h-full bg-white shadow-[0_0_10px_rgba(0,0,0,0.5)]" />

        {/* Center Circular Handle Pill */}
        <div
          className={`absolute w-9 h-9 rounded-full bg-surface border-2 border-primary shadow-xl flex items-center justify-center text-primary transition-transform duration-100 ${
            isDragging ? 'scale-110 ring-4 ring-primary/30' : 'hover:scale-105'
          }`}
        >
          <ChevronsLeftRight className="w-4 h-4" />
        </div>
      </div>

      {/* 4. Frosted Status Badges */}
      <div className="absolute top-4 left-4 z-10 pointer-events-none">
        <span className="px-3 py-1 rounded-full text-[11px] font-bold bg-surface/80 border border-surface-border text-foreground shadow-sm backdrop-blur-md">
          {beforeLabel}
        </span>
      </div>

      <div className="absolute top-4 right-4 z-10 pointer-events-none">
        <span className="px-3 py-1 rounded-full text-[11px] font-bold bg-primary text-primary-fg shadow-sm backdrop-blur-md">
          {afterLabel}
        </span>
      </div>
    </div>
  );
};
